import React from "react";
import locale from "@bonliva-crm/locale";
import { FieldProps as FieldPropsBasic } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import yup from "yup";
import { toJS } from "mobx";
import { Observer } from "mobx-react";
import {
  Button,
  Divider,
  FormControl,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core";
import { store } from "@store";
import { UserAvatarChip } from "@containers";
import {
  ContractFormStore,
  FormData
} from "@consultants/ConsultantBookingContract/ContractFormStore";
import { DepartmentSelect, WithTooltip } from "@common";
import { Add, AddCircle, ArrowDropDown, Error, Remove } from "@material-ui/icons";
import { Checkbox, Date, RadioGroup, Select, ToggleButtonGroup } from "@common/formx";
import { Text } from "@common/formx_item_form";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import {
  contractTypeOptions,
  priceOptions,
  scheduleOptions
} from "@consultants/ConsultantBookingContract/utils";
import {
  AddressInput,
  ConsultantContractBonus,
  ContractsPriceTemplateType,
  Currency,
  CustomerType
} from "@graphql";
import CustomerSelector from "@containers/VirtualSelector/CustomerSelector/CustomerSelector";
import { professions } from "@bonliva-crm/common";
import { moment } from "@bonliva-crm/common";
import Alert from "@material-ui/lab/Alert/Alert";
import SelectCommon from "@common/Select";
import { IntroductionPriceInput } from "@consultants/ConsultantContract/PriceInputs";
import SwitchCommon from "@common/forms/common/Switch";
import { getExpandedArchivedGroup } from "../utils";
import { ContractsPriceTemplateEditorForm } from "../../config_editor/ContractsPriceTemplates/ContractsPriceEditorForm";
import { isEqual } from "lodash";
import WeeklyHoursWorkedBonus from "@consultants/components/Bonus/WeeklyHoursWorkedBonus";
import WorkedHourBonus from "@consultants/components/Bonus/WorkedHourBonus";
import OvertimeBonus from "@consultants/components/Bonus/OvertimeBonus";
import CustomBonus from "@consultants/components/Bonus/CustomBonus";

type FieldProps = FieldPropsBasic<FormData, ContractFormStore>;

const field = ({
  name,
  order = 0,
  show = () => true,
  schema,
  isGroup = false,
  gridProps,
  xs = 4,
  md,
  render
}: FieldProps) => ({
  name,
  order,
  show,
  schema,
  isGroup,
  gridProps,
  xs,
  md,
  render
});

export const responsiblePerson: FieldProps = field({
  xs: 12,
  name: "ResponsiblePersonId",
  schema: yup
    .string()
    .required(locale.fieldIsMandatory(locale["Responsible BA"]))
    .nullable(true),
  render: (form, { store: { setResponsiblePerson } }) => {
    return (
      <div>
        <Typography variant="h6" gutterBottom>
          {form.getError("ResponsiblePersonId") ? (
            <span style={{ color: "#F65629" }}>
              {form.getError("ResponsiblePersonId")}
            </span>
          ) : (
            locale.SelectContactPerson2
          )}
        </Typography>

        <Button
          variant="contained"
          color="secondary"
          onClick={() =>
            store().ui.userSelector.open({
              includeActiveUser: true,
              removeDisabled: true,
              selectedUsers: [form.values.ResponsiblePersonId],
              onSelect: (user) => {
                setResponsiblePerson(user._id);
              }
            })
          }
        >
          {locale.Select}
        </Button>

        {form.values.ResponsiblePersonId && (
          <span style={{ paddingLeft: 10 }}>
            <UserAvatarChip size="normal" />
          </span>
        )}
      </div>
    );
  }
});

export const profession: FieldProps = field({
  xs: 12,
  name: "Profession",
  schema: yup
    .string()
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Profession)),
  render: (form, { store: { allProfessions } }) => (
    <Select
      label={locale.Profession}
      name="Profession"
      key={"Profession"}
      clearable={false}
      multi={false}
      transformValue={(v) => v.value}
      options={allProfessions.toLabelValue(professions.getProfessionDisplayName)}
    />
  )
});

export const contactPerson: FieldProps = field({
  xs: 12,
  name: "ContactPerson",
  schema: yup
    .object<any>()
    .shape({
      Name: yup.string().required(locale.fieldIsMandatory(locale.ContactPersonName)),
      // Title: yup.string().required(locale.fieldIsMandatory(locale.ContactPersonTitle)),
      // Phone: yup.string().required(locale.fieldIsMandatory(locale.ContactPersonPhone)),
      Email: yup
        .string()
        .email()
        .required(locale.fieldIsMandatory(locale.ContactPersonEmail))
    })
    .nullable(true)
    .required(locale.fieldIsMandatory(locale["Contact person"])),
  render: (form, { store: { allOfContactPerson } }) => {
    return (
      <Grid container item spacing={2} key={"contact person"}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel htmlFor="person">
              <WithTooltip
                placement={"top"}
                title={
                  <div style={{ fontSize: 18, padding: 10, lineHeight: "18px" }}>
                    {locale.RecipientsTooltip1}
                  </div>
                }
                aria-label="add"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: 12
                  }}
                >
                  {locale["Contact person"]}
                  <Error style={{ fontSize: 16 }} />
                </div>
              </WithTooltip>
            </FormLabel>
            <Select
              fullWidth
              id="person"
              label={""}
              name="ContactPersonRef"
              options={allOfContactPerson}
            />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <Text label={locale.ContactPersonName} name="ContactPerson.Name" required />
        </Grid>
        <Grid item xs={4}>
          <Text label={locale.ContactPersonTitle} name="ContactPerson.Title" required />
        </Grid>
        <Grid item xs={4}>
          <Text label={locale.ContactPersonPhone} name="ContactPerson.Phone" required />
        </Grid>
        <Grid item xs={4}>
          <Text label={locale.ContactPersonEmail} name="ContactPerson.Email" required />
        </Grid>
      </Grid>
    );
  }
});

export const recipients: FieldProps = field({
  xs: 12,
  name: "Recipients",
  schema: yup.array().nullable(true),
  render: (form, { store: { allOfContactPersonFullInfo } }) => {
    return (
      <Autocomplete
        multiple
        id="tags-standard"
        freeSolo
        value={form.values.Recipients}
        onChange={(e, values) => form.setValue("Recipients", values)}
        options={toJS(allOfContactPersonFullInfo)?.map((x) => x.Email)}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <ArrowDropDown
                      style={{
                        top: "calc(50% - 8px)",
                        right: 30,
                        position: "absolute"
                      }}
                      fontSize={"inherit"}
                    />
                  </>
                )
              }}
              fullWidth
              // onChange={e => form.setValue("Recipients", e.target.value)}
              // variant="standard"
              InputLabelProps={{ shrink: true }}
              label={
                <WithTooltip
                  placement={"top"}
                  title={
                    <div style={{ fontSize: 18, padding: 10, lineHeight: "18px" }}>
                      {locale.RecipientsTooltip2}
                    </div>
                  }
                  aria-label="add"
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {locale.Recipients}
                    <Error />
                  </div>
                </WithTooltip>
              }
              placeholder={locale.PressEnterToAddValue}
            />
          );
        }}
      />
    );
  }
});

export const otherInfo: FieldProps = field({
  xs: 12,
  name: "OtherInfo",
  schema: yup.string(),
  render: () => {
    return (
      <Text
        label={locale.OtherInfo}
        name="OtherInfo"
        TextFieldProps={{
          multiline: true,
          helperText: locale["Press Enter for a new line"]
        }}
      />
    );
  }
});

export const period: FieldProps = field({
  xs: 12,
  name: "Period",
  schema: yup.string().required(locale.fieldIsMandatory(locale.PeriodDesc2)),
  render: () => {
    return <Text label={locale.PeriodDesc2} name="Period" />;
  }
});

export const schedule: FieldProps = field({
  xs: 12,
  name: "Schedule",
  schema: yup.string().required(locale.fieldIsMandatory(locale.Schedule)),
  render: ({ values }) => {
    return (
      <RadioGroup
        name="Schedule"
        label={locale.Schedule}
        customValue
        initCustomValue={values.Schedule}
        TextFieldProps={{
          placeholder: locale.Schedule,
          multiline: true,
          helperText: locale["Press Enter for a new line"]
        }}
        ControlProps={{ fullWidth: true }}
        options={scheduleOptions}
      />
    );
  }
});

export const price: FieldProps = field({
  xs: 12,
  name: "Price",
  schema: yup.string().nullable(true).required(locale.fieldIsMandatory(locale.Price)),
  render: ({ values }) => {
    return (
      <RadioGroup
        name="Price"
        label={locale.Price}
        customValue
        initCustomValue={values.Price}
        TextFieldProps={{
          placeholder: locale.Price,
          multiline: true,
          helperText: locale["Press Enter for a new line"]
        }}
        ControlProps={{ fullWidth: true }}
        options={priceOptions}
      />
    );
  }
});

export const contractType: FieldProps = field({
  xs: 12,
  name: "ContractType",
  schema: yup
    .string()
    .required(locale.fieldIsMandatory(locale.ContractType))
    .nullable(true),
  render: ({ values }, { store }) => {
    const opt =
      store.tenant === "wcc"
        ? contractTypeOptions.filter((x) => x.value !== "Outsource")
        : contractTypeOptions;
    return (
      <ToggleButtonGroup
        ButtonProps={{ disabled: store.action === "duplicate" }}
        name="ContractType"
        label={locale.ContractType}
        options={opt}
      />
    );
  }
});

export const customerSelector: FieldProps = field({
  xs: 12,
  name: "HospitalRef",
  schema: yup
    .object<LabelValue>()
    .shape({
      label: yup.string(),
      value: yup.string()
    })
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Hospital)),
  render: (form, { store: { setHospital } }) => {
    return (
      <>
        <CustomerSelector
          fullWidth
          label={locale["Customer name"]}
          required={
            form.values.ContractType === "Outsource" ||
            form.values.ContractType === "Confirmation"
          }
          error={form.getError("HospitalRef")}
          disabledFilterFields={["customerType"]}
          initialFilter={{
            customerType: CustomerType.Hospital
          }}
          onChange={(v) => setHospital(v)}
          value={form.values.HospitalRef}
        />
        <SwitchCommon
          key={"hideHospitalName"}
          switchProps={{
            color: "secondary",
            disabled: !!form.values.hideDeptName
          }}
          value={form.values.hideHospitalName}
          onChange={(v) => form.setValue("hideHospitalName", v)}
          label={locale.hideHospitalName}
        />
      </>
    );
  }
});

export const departmentSelector: FieldProps = field({
  xs: 12,
  name: "DepartmentsRefs",
  schema: yup.string().nullable(true),
  render: (form, { store: { allDepartments, setDepartments } }) => {
    return (
      allDepartments &&
      form.values.HospitalRef && (
        <>
          <DepartmentSelect
            clearable={false}
            multi={false}
            optionType={"plain"}
            disabled={!form.values.HospitalRef}
            error={form.getError("DepartmentsRefs")}
            options={allDepartments}
            onChange={setDepartments}
            value={form.values.DepartmentsRefs}
          />
          <SwitchCommon
            key={"hideDeptName"}
            switchProps={{
              color: "secondary",
              disabled:
                !!form.values.hideHospitalName ||
                !form.values.DepartmentsRefs ||
                form.values.DepartmentsRefs?.length === 0
            }}
            value={form.values.hideDeptName}
            onChange={(v) => form.setValue("hideDeptName", v)}
            label={locale.hideDeptName}
          />
        </>
      )
    );
  }
});

export const aoeSelector: FieldProps = field({
  xs: 6,
  name: "AreaOfExpertise",
  schema: yup
    .array()
    .max(3, locale.maxCount(3))
    .required(locale.fieldIsMandatory(locale.AreasOfExpertise))
    .of(yup.string()),
  render: (form, { store: { allAreasOfExpertise } }) => {
    return (
      <Select
        label={locale.AreaOfExpertise}
        name="AreaOfExpertise"
        multi
        options={allAreasOfExpertise.toLabelValue()}
      />
    );
  }
});

export const address: FieldProps = field({
  xs: 12,
  name: "Address",
  schema: yup
    .object<AddressInput>()
    .shape({
      City: yup
        .string()
        .required(
          "Kundprofilen saknar angiven stad, vänligen gå till kundprofilen och lägg till detta för att fortsätta."
        )
      // Country: yup.string().required(locale.fieldIsMandatory(locale.Country)),
      // StreetName: yup.string().required(locale.fieldIsMandatory(locale.StreetName)),
      // ZipCode: yup.string().required(locale.fieldIsMandatory(locale.ZipCode))
    })
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Address)),
  render: (form) => {
    const isError = form.getError("OrganizationNumber") || form.getError("Address.City");
    return (
      form.values.HospitalRef && (
        <FormControl error={!!isError} fullWidth key={"BonusPrice"}>
          <FormLabel>{form.getError("Address.City")}</FormLabel>
          <FormLabel>{form.getError("OrganizationNumber")}</FormLabel>
        </FormControl>
      )
    );
  }
});

const gte = (refName: string) => {
  return yup
    .number()
    .typeError(locale.fieldIsMandatory(" "))
    .positive()
    .moreThan(yup.ref(refName), locale.greaterThan("${more}"))
    .required(locale.fieldIsMandatory(" "));
};

export const priceRecruitment: FieldProps = field({
  xs: 12,
  show: () => false,
  name: "PriceRecruitment",
  schema: yup
    .object<any>()
    .shape({
      approve: yup
        .bool()
        .oneOf(
          [true],
          locale.fieldIsMandatory(locale.fieldIsMandatory(locale.ApproveRecruitmentFees))
        ),
      R_0_159: gte("R_160_499"),
      R_160_499: gte("R_500_699"),
      R_500_699: gte("R_700_899"),
      R_700_899: gte("R_900_1200"),
      R_900_1200: yup
        .number()
        .typeError(locale.fieldIsMandatory(" "))
        .positive()
        .required(locale.fieldIsMandatory(" "))
    })
    .nullable(true)
    .required(locale.fieldIsMandatory(locale.Address)),
  render: ({ values, errors }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const PriceRecruitmentApproveErr = !!errors.PriceRecruitment?.approve;
    return (
      <Grid container item spacing={2}>
        <Grid item xs={4}>
          <Select
            clearable={false}
            label={locale.Currency}
            name={`PriceRecruitment.Currency`}
            options={Object.values(Currency)
              .filter((x) => x === "SEK")
              .map((v) => ({
                label: locale.currency[v],
                value: v
              }))}
          />
        </Grid>
        <Grid item xs={4}>
          <Text
            label={`0-160 ${locale.hoursWorked}`}
            name="PriceRecruitment.R_0_159"
            TextFieldProps={{
              type: "number",
              InputProps: {
                endAdornment: `${locale.currency[values.PriceRecruitment.Currency]}`
              }
            }}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <Text
            label={`160-499 ${locale.hoursWorked}`}
            name="PriceRecruitment.R_160_499"
            TextFieldProps={{
              type: "number",
              InputProps: {
                endAdornment: `${locale.currency[values.PriceRecruitment.Currency]}`
              }
            }}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <Text
            label={`500-699 ${locale.hoursWorked}`}
            name="PriceRecruitment.R_500_699"
            TextFieldProps={{
              type: "number",
              InputProps: {
                endAdornment: `${locale.currency[values.PriceRecruitment.Currency]}`
              }
            }}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <Text
            label={`700-899 ${locale.hoursWorked}`}
            name="PriceRecruitment.R_700_899"
            TextFieldProps={{
              type: "number",
              InputProps: {
                endAdornment: `${locale.currency[values.PriceRecruitment.Currency]}`
              }
            }}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <Text
            label={`900-1200 ${locale.hoursWorked}`}
            name="PriceRecruitment.R_900_1200"
            TextFieldProps={{
              type: "number",
              InputProps: {
                endAdornment: `${locale.currency[values.PriceRecruitment.Currency]}`
              }
            }}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Checkbox
            name={"PriceRecruitment.approve"}
            label={
              <Typography color={PriceRecruitmentApproveErr ? "error" : "initial"}>
                {locale.ApproveRecruitmentFees}
              </Typography>
            }
          />
        </Grid>
      </Grid>
    );
  }
});

export const startDate: FieldProps = field({
  name: "StartDate",
  schema: yup.string().nullable(true).required(locale.fieldIsMandatory(locale.StartDate)),
  render: () => (
    <Date
      utc
      key={"StartDate"}
      DatePickerProps={{
        startDate: moment().toDate(),
        shouldCloseOnSelect: true
      }}
      name="StartDate"
      // transformValue={v => moment(v).format("YYYY-MM-DD")}
      label={locale.StartDate}
    />
  )
});

export const endDate: FieldProps = field({
  name: "EndDate",
  schema: yup
    .string()
    .nullable(true)
    .test("EndDate", locale.EndDateAfterStartDate, function testStartDate(e) {
      const { StartDate: s } = this.parent;
      if (s && e) {
        return moment(e).isSameOrAfter(moment(s));
      }
      return true;
    })
    .required(locale.fieldIsMandatory(locale.EndDate)),
  render: ({ values }) => (
    <Date
      utc
      key={"EndDate"}
      DatePickerProps={{
        minDate: moment(values.StartDate).toDate(),
        startDate: moment().toDate(),
        shouldCloseOnSelect: true
      }}
      name="EndDate"
      label={locale.EndDate}
    />
  )
});

export const specificRequirements: FieldProps = field({
  xs: 12,
  name: "SpecificRequirements",
  schema: yup.array().of(yup.string()),
  // show: (form, { store: { isShowAdditionFields } }) => isShowAdditionFields,
  render: ({ values }, { store: { addRequirements, removeRequirements } }) => (
    <FormControl fullWidth key={"SpecificRequirements"}>
      <FormLabel component="legend">
        {`${locale.SpecificRequirementsForTheCompetence} ${locale.SpecificRequirementsForTheCompetenceDesc}`}
      </FormLabel>
      {values.SpecificRequirements?.map((el, i) => (
        <Grid key={i} item container spacing={2} xs={12}>
          <Grid item xs={6}>
            <Text
              name={`SpecificRequirements[${i}]`}
              TextFieldProps={{
                multiline: true,
                helperText: locale["Press Enter for a new line"]
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <IconButton
              aria-label="add"
              onClick={() => addRequirements("SpecificRequirements")}
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="delete"
              disabled={values.SpecificRequirements?.length === 1}
              onClick={() => removeRequirements("SpecificRequirements", i)}
            >
              <Remove fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </FormControl>
  )
});
export const signedOn: FieldProps = field({
  name: "SignedOn",
  xs: 8,
  schema: yup.string().nullable(true),
  render: () => {
    return (
      <Date
        utc
        key={"SignedOn"}
        DatePickerProps={{
          startDate: moment().toDate(),
          shouldCloseOnSelect: true
        }}
        name="SignedOn"
        label={locale.SignedOn}
      />
    );
  }
});

export const bonusRecords: FieldProps = field({
  xs: 12,
  name: "Bonus",
  show: () => false,
  schema: yup.array().of(
    yup.lazy((item: ConsultantContractBonus) => {
      if (!item) return yup.mixed();

      if (isEqual(item.Type, "WeeklyHoursWorkedBonus")) {
        return WeeklyHoursWorkedBonus.ValidationSchema(false);
      } else if (isEqual(item.Type, "WorkedHourBonus")) {
        return WorkedHourBonus.ValidationSchema(false);
      } else if (isEqual(item.Type, "OvertimeBonus")) {
        return OvertimeBonus.ValidationSchema(false);
      } else if (isEqual(item.Type, "CustomBonus")) {
        return CustomBonus.ValidationSchema(false);
      }

      return yup.mixed();
    })
  ),
  render: (
    form,
    { store: { bonusTypes, addBonusRecord, removeBonusRecord, handleChangeBonusType } }
  ) => {
    return (
      <FormControl fullWidth error={!!form.errors.BasicPrice} key={"Bonus"}>
        <FormLabel component="legend">{locale.Bonuses}</FormLabel>
        <Grid container spacing={2} xs={12} alignItems={"center"}>
          <Grid item xs={2}>
            <SelectCommon
              fullWidth
              clearable
              name={`bonusType`}
              key={`bonusType`}
              label={locale.BonusType}
              value={form.values.bonusType}
              onChange={handleChangeBonusType}
              options={bonusTypes?.map((x) => ({ label: locale.bonuses[x], value: x }))}
            />
          </Grid>
          <Grid item xs={2}>
            <Button disabled={!form.values.bonusType} onClick={() => addBonusRecord()}>
              {locale.AddBonus}
            </Button>
          </Grid>

          {form.values.Bonus?.length > 0 ? (
            <Grid item xs={12}>
              <Divider style={{ margin: "5px 0" }} />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            {form.values.Bonus?.map((bonusData, i) => {
              if (isEqual(bonusData.Type, "WeeklyHoursWorkedBonus")) {
                return (
                  <WeeklyHoursWorkedBonus
                    index={i}
                    withSalaryType={false}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                  />
                );
              } else if (isEqual(bonusData.Type, "WorkedHourBonus")) {
                return (
                  <WorkedHourBonus
                    index={i}
                    withSalaryType={false}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                  />
                );
              } else if (isEqual(bonusData.Type, "OvertimeBonus")) {
                return (
                  <OvertimeBonus
                    index={i}
                    withSalaryType={false}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                  />
                );
              } else if (isEqual(bonusData.Type, "CustomBonus")) {
                return (
                  <CustomBonus
                    index={i}
                    withSalaryType={false}
                    data={bonusData}
                    removeBonus={() => removeBonusRecord(i)}
                  />
                );
              }

              return null;
            })}
          </Grid>
        </Grid>
      </FormControl>
    );
  }
});

export const pricesRecords: FieldProps = field({
  xs: 12,
  name: "Salary",
  show: () => false,
  isGroup: true,
  schema: {
    BasicPrice: yup
      .number()
      .nullable(true)
      .required(locale.fieldIsMandatory(locale.SalaryCompensationBookingContract)),
    SpecificPeriodPrices: yup.array().of(
      yup.object().shape({
        price: yup
          .number()
          .nullable(true)
          .required(locale.fieldIsMandatory(locale.Addition)),
        startDate: yup
          .string()
          .nullable(true)
          .required(locale.fieldIsMandatory(locale.StartDate)),
        endDate: yup
          .string()
          .nullable(true)
          .test("EndDate", locale.EndDateAfterStartDate, function testStartDate(e) {
            const { startDate: s } = this.parent;
            if (s && e) {
              return moment(e).isSameOrAfter(moment(s));
            }
            return true;
          })
          .required(locale.fieldIsMandatory(locale.EndDate))
      })
    ),
    IntroductionPrice: yup
      .array()
      .of(
        yup.object().shape({
          Sum: yup.number().nullable(true).required(locale.fieldIsMandatory(locale.Sum)),
          Description: yup
            .string()
            .nullable(true)
            .required(locale.fieldIsMandatory(locale.Description))
        })
      )
      .nullable(true)
  },
  render: (
    form,
    {
      store: {
        addSalary,
        duplicateSalary,
        pushToArr,
        removeByIndex,
        handleChangeTemplate,
        defaultTemplateWarning,
        handleChangeFavTemplate,
        handleChangeCustomerTemplate,
        templates,
        customerPriceOptions,
        fav,
        diffBetweenCustomerTemplate,
        remSpecificPeriodPrice,
        addSpecificPeriodPrice,
        validationResult
      }
    }
  ) => {
    // const disableAddSalary =
    //   form?.values.Overtime === ConsultantContractOvertimeOpt.SKLBasedOvertime;
    return (
      <>
        {diffBetweenCustomerTemplate && form.values.customerTemplate && (
          <Alert style={{ margin: "10px 0" }} severity="warning">
            {locale.PriceListDiffFromCustomerTemplate}
          </Alert>
        )}
        {defaultTemplateWarning && (
          <Alert style={{ margin: "10px 0" }} severity="warning">
            {defaultTemplateWarning}
          </Alert>
        )}
        <FormControl fullWidth error={!!form.errors.BasicPrice} key={"Salary"}>
          <FormLabel component="legend">
            {locale.SalaryCompensationBookingContract}
          </FormLabel>
          <SelectCommon
            // clearable={false}
            name={`template`}
            key={`template`}
            label={locale.Template}
            fullWidth
            value={form.values.template}
            onChange={handleChangeTemplate}
            options={templates?.map((x) => ({ label: x.name, value: x._id }))}
          />
          <SelectCommon
            // clearable={false}
            name={`favTemplate`}
            key={`favTemplate`}
            label={locale.FavTemplate}
            fullWidth
            value={form.values.favTemplate}
            onChange={handleChangeFavTemplate}
            options={fav?.map((x) => ({ label: x.name, value: x._id }))}
          />
          <SelectCommon
            showSearchInput={false}
            disablePortal={false}
            label={locale.ConnectedCustomer}
            fullWidth
            nested
            groupBy="parent"
            onChange={handleChangeCustomerTemplate}
            options={customerPriceOptions}
            value={form.values.customerTemplate}
            expanded={getExpandedArchivedGroup(
              form.values.customerTemplate,
              customerPriceOptions
            )}
          />
          <FormGroup>
            <Text
              type="number"
              name={`BasicPrice`}
              label={locale.Compensation}
              TextFieldProps={{
                inputMode: "numeric"
              }}
            />
            {form.values.SpecificPeriodPrices?.length > 0 ? (
              <Grid container spacing={2}>
                {form.values.SpecificPeriodPrices.map((x, i) => {
                  const name = `SpecificPeriodPrices[${i}]`;
                  return (
                    <React.Fragment key={name}>
                      <Grid item xs={2}>
                        <Text
                          name={`${name}.price`}
                          label={locale.Addition}
                          TextFieldProps={{
                            type: "number",
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position={"start"}>
                                  <Add />
                                </InputAdornment>
                              )
                            }
                            // error: !!form.errors.BasicPrice
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Text name={`${name}.title`} label={locale.Title} />
                      </Grid>
                      <Grid item xs={3}>
                        <Date
                          utc
                          key={"StartDate"}
                          DatePickerProps={{
                            minDate: moment(form.values.StartDate).toDate(),
                            maxDate: moment(form.values.EndDate).toDate(),
                            shouldCloseOnSelect: true
                          }}
                          name={`${name}.startDate`}
                          // transformValue={v => moment(v).format("YYYY-MM-DD")}
                          label={locale.StartDate}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Date
                          key={"EndDate"}
                          DatePickerProps={{
                            minDate: moment(form.values.StartDate).toDate(),
                            maxDate: moment(form.values.EndDate).toDate(),
                            startDate: moment().toDate(),
                            shouldCloseOnSelect: true
                          }}
                          name={`${name}.endDate`}
                          label={locale.EndDate}
                        />
                      </Grid>
                      <Grid item xs={1} style={{ padding: "10px 0" }}>
                        <IconButton onClick={() => remSpecificPeriodPrice(i)}>
                          <Remove />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </Grid>
            ) : (
              <Grid item xs={3} style={{ padding: "10px 0" }}>
                <Button
                  onClick={addSpecificPeriodPrice}
                  color={"secondary"}
                  variant={"contained"}
                  startIcon={<AddCircle />}
                >
                  {locale.AddAdditionPeriodPrice}
                </Button>
              </Grid>
            )}

            <Observer>
              {() => {
                return (
                  <ContractsPriceTemplateEditorForm<FormData>
                    formStore={form}
                    // duplicateRecord={item => editorStore.duplicateRecord(item, name)}
                    duplicateRecord={(item) => duplicateSalary(item)}
                    // addBlankRecord={() => editorStore.addBlankRecord(name)}
                    addBlankRecord={addSalary}
                    type={ContractsPriceTemplateType.ConsultantContract}
                    // loading={}
                    name={`SalaryRecords`}
                    holidaysName={"highHolidays"}
                    variant={"consultantContract"}
                    validationResult={validationResult}
                  />
                );
              }}
            </Observer>
          </FormGroup>
        </FormControl>
        {diffBetweenCustomerTemplate && form.values.customerTemplate && (
          <Alert style={{ margin: "10px 0" }} severity="warning">
            {locale.PriceListDiffFromCustomerTemplate}
          </Alert>
        )}
        <IntroductionPriceInput
          name={"IntroductionPrice"}
          // error={form.getError("IntroductionPrice")}
          form={form}
          add={pushToArr}
          remove={removeByIndex}
        />
      </>
    );
  }
});

export const sithsCardNumber: FieldProps = field({
  xs: 4,
  name: "sithsCardNumber",
  schema: yup.string().nullable(true),
  render: () => {
    return <Text label={`SITHS ${locale.CardNumber}`} name="sithsCardNumber" />;
  }
});

export const callNumber: FieldProps = field({
  xs: 4,
  name: "callNumber",
  schema: yup.string().nullable(true),
  render: () => {
    return <Text label={locale.CallNumber} name="callNumber" />;
  }
});
